import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  Timer,
  Palette,
  Wind,
  Heart,
  RefreshCw,
  Music,
  Headphones,
} from "lucide-react";
import Layout from "../../layout";

const WellnessActivityDetail = ({
  activity: propActivity,
  onBack,
  onComplete,
}) => {
  const [activity, setActivity] = useState(propActivity);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [breathingPhase, setBreathingPhase] = useState("inhale");
  const [breathingCount, setBreathingCount] = useState(4);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [isBreathingActive, setIsBreathingActive] = useState(false);
  const [activeEmotionTool, setActiveEmotionTool] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [emotionToolTimer, setEmotionToolTimer] = useState(null);
  const [isEmotionToolActive, setIsEmotionToolActive] = useState(false);
  const audioRef = useRef(null);
  const playlistAudioRef = useRef(null);
  const breathingIntervalRef = useRef(null);
  const emotionToolIntervalRef = useRef(null);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [isPlaylistPlaying, setIsPlaylistPlaying] = useState(false);
  const [playlistCurrentTime, setPlaylistCurrentTime] = useState(0);
  const [playlistDuration, setPlaylistDuration] = useState(0);
  const [isMeditating, setIsMeditating] = useState(false);
  const [meditationTimer, setMeditationTimer] = useState(0);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  const [colorTransition, setColorTransition] = useState(false);
  const meditationIntervalRef = useRef(null);
  const colorCycleRef = useRef(null);
  const [currentToolsSet, setCurrentToolsSet] = useState([]);

  const getRandomTools = (allTools, excludeUsed = true) => {
    if (!allTools || allTools.length === 0) return [];

    let availableTools = [...allTools];

    const usedToolsKey = "usedEmotionTools";
    const usedTools = JSON.parse(sessionStorage.getItem(usedToolsKey) || "[]");

    if (excludeUsed && usedTools.length < allTools.length) {
      availableTools = allTools.filter(
        (tool) => !usedTools.includes(tool.name)
      );
    }

    if (availableTools.length < 4) {
      availableTools = [...allTools];
      sessionStorage.setItem(usedToolsKey, JSON.stringify([]));
    }

    const shuffled = availableTools.sort(() => 0.5 - Math.random());
    const selectedTools = shuffled.slice(0, 4);

    const newUsedTools = [
      ...usedTools,
      ...selectedTools.map((tool) => tool.name),
    ];
    sessionStorage.setItem(usedToolsKey, JSON.stringify(newUsedTools));

    return selectedTools;
  };

  useEffect(() => {
    const storedActivity = localStorage.getItem("selectedActivity");
    if (storedActivity && !propActivity) {
      try {
        const parsedActivity = JSON.parse(storedActivity);
        setActivity(parsedActivity);

        // If it's emotional regulation, set up random tools
        if (
          parsedActivity.id === "emotional-regulation" &&
          parsedActivity.allTools
        ) {
          const randomTools = getRandomTools(parsedActivity.allTools);
          setCurrentToolsSet(randomTools);
        }

        // Clear the stored activity after loading
        localStorage.removeItem("selectedActivity");
      } catch (error) {
        console.error("Error parsing stored activity:", error);
      }
    } else if (propActivity) {
      // If it's emotional regulation from props, set up random tools
      if (propActivity.id === "emotional-regulation" && propActivity.allTools) {
        const randomTools = getRandomTools(propActivity.allTools);
        setCurrentToolsSet(randomTools);
      }
    }
  }, [propActivity]);

  const handleCompleteActivity = () => {
    onComplete && onComplete();
  };

  // Audio event handlers
  const handlePlayPause = (sound) => {
    if (currentTrack === sound.name && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Stop playlist if playing
      if (playlistAudioRef.current) {
        playlistAudioRef.current.pause();
        setIsPlaylistPlaying(false);
        setCurrentPlaylist(null);
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }
      setCurrentTrack(sound.name);
      setIsPlaying(true);

      const audioSrc = sound.file;

      //console.log("Loading audio from:", audioSrc, "Type:", typeof sound.file);
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      audio.volume = isMuted ? 0 : volume;
      audio.loop = true;

      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
      });

      audio.addEventListener("timeupdate", () => {
        setCurrentTime(audio.currentTime);
      });

      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTrack(null);
      });

      audio.play().catch(console.error);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : newVolume;
    }
    if (playlistAudioRef.current) {
      playlistAudioRef.current.volume = isMuted ? 0 : newVolume;
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.volume = !isMuted ? 0 : volume;
    }
    if (playlistAudioRef.current) {
      playlistAudioRef.current.volume = !isMuted ? 0 : volume;
    }
  };

  const handlePlaylistPlay = (playlist) => {
    if (!playlist.file) {
      alert(
        `${playlist.name} audio file not available yet. Only Tranquility is currently loaded.`
      );
      return;
    }

    if (currentPlaylist === playlist.name && isPlaylistPlaying) {
      playlistAudioRef.current.pause();
      setIsPlaylistPlaying(false);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        setCurrentTrack(null);
      }

      if (playlistAudioRef.current) {
        playlistAudioRef.current.pause();
      }

      setCurrentPlaylist(playlist.name);
      setIsPlaylistPlaying(true);

      const audio = new Audio(playlist.file);
      playlistAudioRef.current = audio;
      audio.volume = isMuted ? 0 : volume;
      audio.loop = true;

      audio.addEventListener("loadedmetadata", () => {
        setPlaylistDuration(audio.duration);
      });

      audio.addEventListener("timeupdate", () => {
        setPlaylistCurrentTime(audio.currentTime);
      });

      audio.addEventListener("ended", () => {
        setIsPlaylistPlaying(false);
        setCurrentPlaylist(null);
      });

      audio.play().catch(console.error);
    }
  };

  const startBreathingExercise = (technique) => {
    setSelectedTechnique(technique);
    setIsBreathingActive(true);
    setBreathingPhase("inhale");
    setBreathingCount(technique.pattern[0]);

    breathingIntervalRef.current = setInterval(() => {
      setBreathingCount((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          const phases = ["inhale", "hold", "exhale", "hold"];
          const currentIndex = phases.indexOf(breathingPhase);
          const nextIndex = (currentIndex + 1) % phases.length;
          const nextPhase = phases[nextIndex];

          setBreathingPhase(nextPhase);

          return technique.pattern[nextIndex] || technique.pattern[0];
        }
      });
    }, 1000);
  };

  const stopBreathingExercise = () => {
    setIsBreathingActive(false);
    setSelectedTechnique(null);
    if (breathingIntervalRef.current) {
      clearInterval(breathingIntervalRef.current);
    }
  };

  const startEmotionTool = (tool) => {
    setActiveEmotionTool(tool);
    setCurrentStep(0);
    setIsEmotionToolActive(true);

    setEmotionToolTimer(30); // 30 seconds per step

    emotionToolIntervalRef.current = setInterval(() => {
      setEmotionToolTimer((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          // Move to next step
          setCurrentStep((prevStep) => {
            const nextStep = prevStep + 1;
            if (nextStep >= tool.steps.length) {
              stopEmotionTool();
              return prevStep;
            }
            return nextStep;
          });
          return 30; // Reset timer for next step
        }
      });
    }, 1000);
  };

  const stopEmotionTool = () => {
    setIsEmotionToolActive(false);
    setActiveEmotionTool(null);
    setCurrentStep(0);
    setEmotionToolTimer(null);
    if (emotionToolIntervalRef.current) {
      clearInterval(emotionToolIntervalRef.current);
    }
  };

  const nextEmotionStep = () => {
    if (activeEmotionTool && currentStep < activeEmotionTool.steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setEmotionToolTimer(30); // Reset timer
    } else {
      stopEmotionTool();
    }
  };

  const prevEmotionStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setEmotionToolTimer(30); // Reset timer
    }
  };

  // Color therapy meditation handlers
  const startMeditation = () => {
    setIsMeditating(true);
    setMeditationTimer(300); // 5 minutes meditation
    setCurrentColorIndex(0); // Start with first color

    // Main timer countdown
    meditationIntervalRef.current = setInterval(() => {
      setMeditationTimer((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          stopMeditation();
          return 0;
        }
      });
    }, 1000);

    colorCycleRef.current = setInterval(() => {
      setColorTransition(true);
      setTimeout(() => {
        setCurrentColorIndex((prev) => (prev + 1) % activity.colors.length);
        setColorTransition(false);
      }, 500);
    }, 8000);
  };

  const stopMeditation = () => {
    setIsMeditating(false);
    setMeditationTimer(0);
    setCurrentColorIndex(0);
    setColorTransition(false);
    if (meditationIntervalRef.current) {
      clearInterval(meditationIntervalRef.current);
    }
    if (colorCycleRef.current) {
      clearInterval(colorCycleRef.current);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (playlistAudioRef.current) {
        playlistAudioRef.current.pause();
      }
      if (breathingIntervalRef.current) {
        clearInterval(breathingIntervalRef.current);
      }
      if (emotionToolIntervalRef.current) {
        clearInterval(emotionToolIntervalRef.current);
      }
      if (meditationIntervalRef.current) {
        clearInterval(meditationIntervalRef.current);
      }
      if (colorCycleRef.current) {
        clearInterval(colorCycleRef.current);
      }
    };
  }, []);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Wellness Hub
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="text-6xl mb-4">{activity.icon}</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {activity.title}
          </h1>
          <p className="text-gray-600 text-lg">{activity.description}</p>
          <div className="flex justify-center gap-4 mt-4">
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
              {activity.category}
            </span>
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
              {activity.duration}
            </span>
          </div>
        </motion.div>

        <div className="space-y-8">
          {activity.id === "nature-sounds" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <h2 className="text-xl font-bold mb-6 text-center">
                Choose Your Nature Sound
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {activity.sounds.map((sound, index) => (
                  <motion.button
                    key={sound.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handlePlayPause(sound)}
                    className={`p-4 rounded-xl transition-all ${
                      currentTrack === sound.name
                        ? "bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-lg"
                        : "bg-gradient-to-r from-blue-50 to-green-50 hover:from-blue-100 hover:to-green-100"
                    }`}
                  >
                    <div className="text-2xl mb-2">{sound.emoji}</div>
                    <div className="font-medium text-sm">{sound.name}</div>
                    <div className="flex justify-center mt-2">
                      {currentTrack === sound.name && isPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {currentTrack && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-gray-50 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handlePlayPause({ name: currentTrack })}
                        className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                      >
                        {isPlaying ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <div>
                        <div className="font-medium text-sm">Now Playing</div>
                        <div className="text-xs text-gray-500">
                          {currentTrack}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleMuteToggle}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {isMuted ? (
                          <VolumeX className="w-4 h-4" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={volume}
                        onChange={(e) =>
                          handleVolumeChange(parseFloat(e.target.value))
                        }
                        className="w-20"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatTime(currentTime)}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-blue-500 h-1 rounded-full transition-all"
                        style={{
                          width: `${
                            duration ? (currentTime / duration) * 100 : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                    <span>{formatTime(duration)}</span>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {activity.id === "breathing-patterns" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <h2 className="text-xl font-bold mb-6 text-center">
                Breathing Techniques
              </h2>

              {!isBreathingActive ? (
                <div className="grid gap-4">
                  {activity.techniques.map((technique, index) => (
                    <motion.div
                      key={technique.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 border border-gray-200 rounded-xl hover:border-blue-300 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{technique.name}</h3>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {technique.pattern.join("-")}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">
                        {technique.purpose}
                      </p>
                      <button
                        onClick={() => startBreathingExercise(technique)}
                        className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Start Exercise
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                >
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      {selectedTechnique.name}
                    </h3>
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {breathingPhase.toUpperCase()}
                    </div>
                    <div className="text-6xl font-mono text-blue-500">
                      {breathingCount}
                    </div>
                  </div>

                  <motion.div
                    animate={{
                      scale:
                        breathingPhase === "inhale"
                          ? 1.2
                          : breathingPhase === "exhale"
                          ? 0.8
                          : 1,
                    }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                    className="w-32 h-32 bg-gradient-to-r from-blue-400 to-green-400 rounded-full mx-auto mb-6 opacity-70"
                  ></motion.div>

                  <button
                    onClick={stopBreathingExercise}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Stop Exercise
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {activity.id === "color-therapy" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <h2 className="text-xl font-bold mb-6 text-center">
                Color Therapy Visualization
              </h2>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center p-6 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200"
              >
                <h3 className="text-xl font-semibold mb-3">
                  Dynamic Color Visualization
                </h3>
                <p className="text-gray-700 mb-6">
                  Experience a flowing journey through therapeutic colors. Each
                  color will cycle automatically, allowing you to absorb their
                  unique healing energies and effects.
                </p>
                {!isMeditating ? (
                  <div>
                    <div className="text-sm text-gray-600 mb-4">
                      Close your eyes and visualize this color filling your
                      entire being...
                    </div>
                    <button
                      onClick={startMeditation}
                      className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      Start 5-Minute Meditation
                    </button>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center relative overflow-hidden"
                    style={{
                      background: `linear-gradient(45deg, ${
                        activity.colors[currentColorIndex]?.hex
                      }20, ${
                        activity.colors[
                          (currentColorIndex + 1) % activity.colors.length
                        ]?.hex
                      }20)`,
                      transition: "background 2s ease-in-out",
                    }}
                  >
                    <div className="absolute inset-0 overflow-hidden">
                      {activity.colors.map((color, index) => (
                        <motion.div
                          key={color.name}
                          className="absolute inset-0"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity:
                              index === currentColorIndex ? [0, 0.6, 0] : 0,
                            scale: index === currentColorIndex ? [0, 2, 3] : 0,
                            rotate: [0, 180, 360],
                          }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          style={{
                            background: `radial-gradient(circle, ${color.hex}40 0%, transparent 70%)`,
                          }}
                        />
                      ))}
                    </div>

                    <div className="relative z-10 p-8">
                      <motion.div
                        className="text-6xl mb-4"
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        🧘‍♀️
                      </motion.div>

                      <motion.h4
                        className="text-xl font-semibold mb-4"
                        style={{
                          color: activity.colors[currentColorIndex]?.hex,
                        }}
                        animate={{ opacity: colorTransition ? 0 : 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {activity.colors[currentColorIndex]?.name} Visualization
                      </motion.h4>

                      <div
                        className="text-4xl font-mono mb-6"
                        style={{
                          color: activity.colors[currentColorIndex]?.hex,
                        }}
                      >
                        {formatTime(meditationTimer)}
                      </div>

                      <div className="relative mb-8">
                        {activity.colors.map((color, index) => (
                          <motion.div
                            key={color.name}
                            className="absolute w-16 h-16 rounded-full"
                            style={{
                              backgroundColor: color.hex + "80",
                              left: `${20 + index * 15}%`,
                              top: `${Math.sin(index) * 20}px`,
                            }}
                            animate={{
                              y: [0, -20, 0],
                              opacity:
                                index === currentColorIndex
                                  ? [0.8, 1, 0.8]
                                  : 0.3,
                              scale:
                                index === currentColorIndex ? [1, 1.3, 1] : 0.8,
                            }}
                            transition={{
                              duration: 2 + index * 0.5,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: index * 0.3,
                            }}
                          />
                        ))}
                      </div>

                      <motion.div
                        className="mb-6 text-base text-gray-700"
                        animate={{ opacity: colorTransition ? 0 : 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        Visualize{" "}
                        {activity.colors[currentColorIndex]?.name.toLowerCase()}{" "}
                        light flowing through your body...
                        <br />
                        <span className="text-sm italic">
                          "{activity.colors[currentColorIndex]?.effect}"
                        </span>
                      </motion.div>

                      <motion.div
                        className="w-40 h-40 rounded-full mx-auto mb-8 relative shadow-2xl"
                        style={{
                          background: `radial-gradient(circle, ${activity.colors[currentColorIndex]?.hex} 0%, ${activity.colors[currentColorIndex]?.hex}60 50%, transparent 100%)`,
                        }}
                        animate={{
                          scale: [1, 1.2, 1],
                          rotate: [0, 360],
                        }}
                        transition={{
                          duration: 8,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        <motion.div
                          className="absolute inset-4 rounded-full"
                          style={{
                            backgroundColor:
                              activity.colors[currentColorIndex]?.hex,
                          }}
                          animate={{
                            opacity: [0.6, 1, 0.6],
                            scale: [0.8, 1, 0.8],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </motion.div>

                      <div className="flex justify-center gap-4">
                        <button
                          onClick={stopMeditation}
                          className="px-8 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl hover:from-red-600 hover:to-pink-600 transition-all shadow-lg"
                        >
                          End Visualization
                        </button>
                      </div>

                      <div className="mt-6 flex justify-center space-x-2">
                        {activity.colors.map((color, index) => (
                          <div
                            key={color.name}
                            className={`w-3 h-3 rounded-full transition-all ${
                              index === currentColorIndex
                                ? "scale-125 ring-2 ring-white"
                                : "opacity-50"
                            }`}
                            style={{ backgroundColor: color.hex }}
                          />
                        ))}
                      </div>

                      <div className="text-xs text-gray-600 mt-2">
                        Colors change every 8 seconds
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}

          {activity.id === "mood-music" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {activity.playlists.map((playlist, index) => (
                  <motion.div
                    key={playlist.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white">
                        <Music className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">
                          {playlist.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {playlist.genre}{" "}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4"></div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePlaylistPlay(playlist)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            currentPlaylist === playlist.name &&
                            isPlaylistPlaying
                              ? "bg-green-500 hover:bg-green-600"
                              : playlist.file
                              ? "bg-purple-500 hover:bg-purple-600"
                              : "bg-gray-400 cursor-not-allowed"
                          } text-white`}
                          disabled={!playlist.file}
                        >
                          {currentPlaylist === playlist.name &&
                          isPlaylistPlaying ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4 ml-0.5" />
                          )}
                        </button>
                        <span className="text-xs text-gray-600">
                          {currentPlaylist === playlist.name &&
                          isPlaylistPlaying
                            ? "Playing..."
                            : playlist.file
                            ? "Play Playlist"
                            : "Coming Soon"}
                        </span>
                      </div>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        {playlist.genre}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activity.id === "emotional-regulation" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <h2 className="text-xl font-bold mb-6 text-center">
                Emotion Regulation Techniques
              </h2>

              {!isEmotionToolActive ? (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <button
                      onClick={() => {
                        if (activity.allTools) {
                          const newTools = getRandomTools(
                            activity.allTools,
                            false
                          ); 
                          setCurrentToolsSet(newTools);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Get New Tools
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Showing 4 of {activity.allTools?.length || 0} available
                      techniques
                    </p>
                  </div>

                  <div className="grid gap-6">
                    {currentToolsSet.map((tool, index) => (
                      <motion.div
                        key={tool.name}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 border border-blue-100 relative"
                      >
                        <div className="absolute top-3 right-3">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              tool.category === "Mindfulness"
                                ? "bg-green-100 text-green-700"
                                : tool.category === "Cognitive"
                                ? "bg-blue-100 text-blue-700"
                                : tool.category === "Physical"
                                ? "bg-orange-100 text-orange-700"
                                : tool.category === "Processing"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-pink-100 text-pink-700"
                            }`}
                          >
                            {tool.category}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-800 mb-3 pr-20">
                          {tool.name}
                        </h3>
                        <div className="grid md:grid-cols-4 gap-3 mb-4">
                          {tool.steps.map((step, stepIndex) => (
                            <div
                              key={stepIndex}
                              className="bg-white/60 rounded-lg p-3 text-center"
                            >
                              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
                                {stepIndex + 1}
                              </div>
                              <div className="text-sm text-gray-700">
                                {step}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => startEmotionTool(tool)}
                          className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Practice {tool.name}
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                >
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      {activeEmotionTool.name}
                    </h3>
                    <div className="text-sm text-gray-600 mb-6">
                      Step {currentStep + 1} of {activeEmotionTool.steps.length}
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${
                            ((currentStep + 1) /
                              activeEmotionTool.steps.length) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>

                    <div className="bg-white rounded-xl p-6 mb-6 shadow-lg">
                      <div className="text-6xl mb-4">
                        {currentStep === 0
                          ? "🧠"
                          : currentStep === 1
                          ? "💭"
                          : currentStep === 2
                          ? "🌱"
                          : "✨"}
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">
                        {activeEmotionTool.steps[currentStep]}
                      </h4>

                      <div className="text-gray-600 mb-4">
                        {currentStep === 0 &&
                          "Take a moment to identify what you're feeling right now. Name the emotion without judgment."}
                        {currentStep === 1 &&
                          "Notice where you feel this emotion in your body. Is there tension, tightness, or other sensations?"}
                        {currentStep === 2 &&
                          "Take slow, deep breaths. With each exhale, imagine releasing some of the intensity."}
                        {currentStep === 3 &&
                          "Consider what this emotion might be telling you. What do you need right now?"}
                      </div>

                      {emotionToolTimer && (
                        <div className="text-3xl font-mono text-blue-500 mb-4">
                          {Math.floor(emotionToolTimer / 60)}:
                          {(emotionToolTimer % 60).toString().padStart(2, "0")}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-center gap-4">
                      <button
                        onClick={prevEmotionStep}
                        disabled={currentStep === 0}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={nextEmotionStep}
                        className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      >
                        {currentStep === activeEmotionTool.steps.length - 1
                          ? "Complete"
                          : "Next Step"}
                      </button>
                      <button
                        onClick={stopEmotionTool}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Stop Practice
                      </button>
                    </div>
                  </div>

                  {currentStep === activeEmotionTool.steps.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-50 border border-green-200 rounded-xl p-6"
                    >
                      <div className="text-4xl mb-3">🎉</div>
                      <h4 className="text-lg font-semibold text-green-800 mb-2">
                        Great work! You've completed this emotion regulation
                        practice.
                      </h4>
                      <p className="text-green-700 text-sm">
                        Regular practice of these techniques can help you better
                        manage difficult emotions and build emotional
                        resilience.
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WellnessActivityDetail;
